import { GOOGLE_OAUTH_ADAPTERS, SCOPE_CHOICE_ADAPTERS, type ConnectionDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, Database, HeartPulse, KeyRound, LogIn, LogOut, Pencil, Plug, Power, Settings2, SquareStack, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { JsonBlock } from '@/components/code-block';
import { ConfigView } from '@/components/config-fields';
import { Confirm } from '@/components/confirm';
import { ConnectionDialog } from '@/components/connection-dialog';
import { ConnectionScopeSection } from '@/components/connection-scope';
import { ConnectionSharingSection } from '@/components/connection-sharing';
import { mcpErrorText, McpToolsSection } from '@/components/mcp-tools';
import { DefinitionList, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { ConnectionStatusBadge } from '@/components/status-badge';
import { ErrorState, LoadingPage } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { isEmptyObject } from '@/lib/format';
import { useApiMutation, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { CONNECTION_TYPE, PERMISSION_LABEL } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';

export function ConnectionDetailPage() {
  const { workspaceId, connectionId } = useParams({ from: '/_authed/w/$workspaceId/connections/$connectionId' });
  const connection = useQuery({ queryKey: qk.connection(connectionId), queryFn: () => api.connections.get(connectionId, workspaceId) });

  if (connection.isPending) return <LoadingPage />;
  if (connection.isError) {
    return (
      <Page>
        <BackLink workspaceId={workspaceId} />
        <ErrorState error={connection.error} onRetry={() => void connection.refetch()} />
      </Page>
    );
  }
  return <ConnectionView connection={connection.data} workspaceId={workspaceId} />;
}

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/w/$workspaceId/connections"
      params={{ workspaceId }}
      className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> {t('All connections')}
    </Link>
  );
}

function ConnectionView({ connection: c, workspaceId }: { connection: ConnectionDetail; workspaceId: string }) {
  const toast = useToast();
  const workspace = useWorkspace(workspaceId);
  const invalidate = [qk.connection(c.id), qk.connections(workspaceId)];
  /** Shared by another workspace (docs/features/connections.md): used here, changed only there. */
  const foreign = c.workspaceId !== workspaceId;

  const check = useApiMutation(() => api.connections.healthCheck(c.id), {
    // For MCP connections the health check also refreshes the cached tool list (qk.connection is its prefix).
    invalidate,
    onSuccess: (res) => {
      if (res.status === 'healthy') toast.success(t('Connection works'), res.healthMessage ?? undefined);
      else
        toast.error(
          t('Connection is failing'),
          res.healthMessage ? (c.type === 'mcp_server' ? mcpErrorText(res.healthMessage) : res.healthMessage) : t('The check failed.'),
        );
    },
  });
  const disabled = c.status === 'disabled';
  const toggle = useApiMutation(() => api.connections.update(c.id, { status: disabled ? 'unknown' : 'disabled' }), {
    invalidate,
    success: disabled ? t('Connection enabled') : t('Connection disabled'),
  });

  const signIn = useApiMutation(() => api.connections.oauthStart(c.id, window.location.origin), {
    invalidate,
    onSuccess: (res) => {
      if (res.authorizationUrl) window.location.assign(res.authorizationUrl);
      else toast.success(t('Signed in'));
    },
  });
  const signOut = useApiMutation(() => api.connections.oauthDisconnect(c.id), { invalidate, success: t('Signed out') });
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: Infinity, retry: false });
  // Back from the authorization server (…?oauth=connected|failed).
  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get('oauth');
    if (!outcome) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (outcome === 'connected') toast.success(t('Signed in'), c.config.adapter ? t('Deskworks can now use this system.') : t('Deskworks can now use this MCP server.'));
    else toast.error(t('Sign-in failed'), c.oauth?.error ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** OAuth sign-in: remote MCP servers, and Deskworks's own adapters with an application registered on the instance. */
  const remoteMcp = c.type === 'mcp_server' && !c.managed && !c.config.adapter;
  const ownClient = typeof c.config.oauthClientId === 'string' && !!c.config.oauthClientId;
  const google = c.type === 'mcp_server' && (GOOGLE_OAUTH_ADAPTERS as readonly unknown[]).includes(c.config.adapter);
  /**
   * Google adapters sign in through Deskworks's OAuth client (no credential to save) or the customer's own one (the
   * saved credential is its client secret).
   */
  const deskworksGoogle = google && !ownClient && !!health.data?.googleOAuth;
  const adapterOAuth = c.type === 'mcp_server' && !!c.config.adapter && (ownClient || deskworksGoogle);
  const canSignIn = remoteMcp || adapterOAuth;
  const asksForSignIn =
    !foreign &&
    ((adapterOAuth && !c.oauth?.connected) ||
    (remoteMcp && !c.oauth?.connected && /unauthori[sz]ed|unauthenticated|\b401\b|invalid_token|authorization header|anmeldung|sign.?in|login required/i.test(c.healthMessage ?? '')));

  const navigate = useNavigate();
  const remove = useApiMutation(() => api.connections.remove(c.id), {
    invalidate: [qk.connections(workspaceId)],
    success: t('Connection deleted'),
    onSuccess: () => void navigate({ to: '/w/$workspaceId/connections', params: { workspaceId } }),
  });

  return (
    <Page>
      <BackLink workspaceId={workspaceId} />
      <PageHeader
        actionsOnTop
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {c.name}
            <ConnectionStatusBadge status={c.status} />
          </span>
        }
        description={c.description || t('{type} connection', { type: CONNECTION_TYPE[c.type]?.label ?? c.type })}
        actions={
          foreign ? (
            <Button size="sm" onClick={() => check.mutate()} loading={check.isPending} disabled={disabled}>
              <HeartPulse /> {t('Check now')}
            </Button>
          ) : c.managed === 'workspace_db' ? (
            <>
              <Button asChild size="sm">
                <Link to="/w/$workspaceId/database" params={{ workspaceId }}>
                  <Database /> {t('Open database')}
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={() => check.mutate()} loading={check.isPending}>
                <HeartPulse /> {t('Check now')}
              </Button>
            </>
          ) : (
          <>
            <ConnectionDialog workspaceId={workspaceId} connection={c}>
              <Button variant="outline" size="sm">
                <Pencil /> {t('Edit')}
              </Button>
            </ConnectionDialog>
            <Button variant="outline" size="sm" onClick={() => toggle.mutate()} loading={toggle.isPending}>
              <Power /> {disabled ? t('Enable') : t('Disable')}
            </Button>
            <Confirm
              title={t('Delete connection “{name}”?', { name: c.name })}
              description={
                c.sharedWith === 'all' || c.sharedWith.length
                  ? t('It will be removed from {n} desks, also in the workspaces it is shared with, and the saved token will be deleted. Agents won’t be able to reach the system afterwards.', { n: c.desks.length })
                  : c.desks.length === 1
                  ? t('It will be removed from 1 desk and the saved token will be deleted. Agents won’t be able to reach the system afterwards.')
                  : t('It will be removed from {n} desks and the saved token will be deleted. Agents won’t be able to reach the system afterwards.', { n: c.desks.length })
              }
              confirmLabel={t('Delete')}
              destructive
              onConfirm={() => remove.mutateAsync()}
            >
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 /> {t('Delete')}
              </Button>
            </Confirm>
            <Button size="sm" onClick={() => check.mutate()} loading={check.isPending} disabled={disabled}>
              <HeartPulse /> {t('Check now')}
            </Button>
          </>
          )
        }
      />

      {foreign && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          {t('Shared by the workspace {workspace}. Desks here may use it; its settings, credential and sign-in are changed there.', { workspace: c.workspaceName ?? '…' })}
        </div>
      )}

      {asksForSignIn && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
          <span className="min-w-0 flex-1">
            {adapterOAuth
              ? t('Sign in with your account there to connect. Deskworks acts as you and keeps the access fresh on its own.')
              : t('This MCP server asks you to sign in. Sign in with your account there, and Deskworks keeps the access fresh on its own.')}
          </span>
          <Button size="sm" onClick={() => signIn.mutate()} loading={signIn.isPending}>
            <LogIn /> {t('Sign in with OAuth')}
          </Button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section icon={<Plug />} title={t('Overview')}>
          <DefinitionList
            items={[
              {
                label: t('Type'),
                value: c.managed === 'workspace_db' ? t('Workspace database ({database}), managed by Deskworks', { database: String(c.config.database ?? '') }) : (CONNECTION_TYPE[c.type]?.label ?? c.type),
              },
              { label: t('Status'), value: <ConnectionStatusBadge status={c.status} /> },
              { label: t('Workspace'), value: (foreign ? c.workspaceName : workspace.data?.name) ?? '…' },
              { label: t('Created'), value: <RelativeTime value={c.createdAt} /> },
            ]}
          />
        </Section>

        <Section icon={<HeartPulse />} title={t('Last check')}
          description={
            c.type === 'mcp_server'
              ? t('Deskworks connects to the MCP server and also reloads the list of tools.')
              : t('Deskworks checks whether the system is reachable and the credentials are correct.')
          }>
          <DefinitionList
            items={[
              { label: t('Checked'), value: <RelativeTime value={c.lastHealthCheckAt} fallback={t('never')} /> },
              {
                label: t('Result'),
                value: c.healthMessage ? (
                  <span className="break-words">{c.type === 'mcp_server' ? mcpErrorText(c.healthMessage) : c.healthMessage}</span>
                ) : c.lastHealthCheckAt ? (
                  t('No message')
                ) : (
                  '—'
                ),
              },
            ]}
          />
        </Section>

        <Section icon={<KeyRound />} title={t('Credentials')} description={t('Stored encrypted and never shown — not even to you.')}>
          {canSignIn && (
            <div className="mb-3 flex flex-col gap-2 border-b pb-3">
              <div className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                  OAuth
                  {c.oauth?.connected ? (
                    <Badge tone="green">{t('signed in')}</Badge>
                  ) : c.oauth ? (
                    <Badge tone="amber">{t('sign in again')}</Badge>
                  ) : (
                    <Badge tone="outline">{t('not used')}</Badge>
                  )}
                  {c.oauth?.connectedAt && (
                    <span className="text-xs text-muted-foreground">
                      <RelativeTime value={c.oauth.connectedAt} />
                      {c.oauth.scope ? ` · ${t('scope {scope}', { scope: c.oauth.scope })}` : ''}
                    </span>
                  )}
                </span>
                {!foreign && (
                <span className="flex shrink-0 gap-1">
                  {c.oauth && (
                    <Button variant="ghost" size="sm" onClick={() => signOut.mutate()} loading={signOut.isPending}>
                      <LogOut /> {t('Sign out')}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => signIn.mutate()} loading={signIn.isPending}>
                    <LogIn /> {c.oauth?.connected ? t('Sign in again') : t('Sign in')}
                  </Button>
                </span>
                )}
              </div>
              {c.oauth?.error && <p className="text-xs text-destructive break-words">{c.oauth.error}</p>}
              {c.oauth && c.hasCredential && !google && (
                <p className="text-xs text-muted-foreground">{t('While signed in with OAuth, the saved token below is not used.')}</p>
              )}
            </div>
          )}
          {!deskworksGoogle && (
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm">
                {c.type === 'mcp_server' && !c.managed && (google ? t('Client secret') : c.config.adapter === 'dataforseo' ? t('Login and password') : 'Token')}
                {c.hasCredential ? <Badge tone="green">{t('saved')}</Badge> : <Badge tone="amber">{t('not saved')}</Badge>}
              </span>
              {!foreign && (
                <ConnectionDialog workspaceId={workspaceId} connection={c}>
                  <Button variant="ghost" size="sm">
                    {c.hasCredential ? t('Replace') : t('Add')}
                  </Button>
                </ConnectionDialog>
              )}
            </div>
          )}
        </Section>

        <Section icon={<SquareStack />} title={t('Used by')} description={t('Desks whose agents are allowed to use this connection.')}>
          {c.desks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('No desk uses this connection yet. Add it on a desk’s page.')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {c.desks.map((d) => (
                <li key={d.id}>
                  <Link
                    to="/w/$workspaceId/desks/$deskId"
                    params={{ workspaceId: d.workspaceId, deskId: d.id }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <span className="flex-1 truncate">
                      {d.name}
                      {d.workspaceId !== workspaceId && <span className="text-muted-foreground"> · {d.workspaceName}</span>}
                    </span>
                    {d.permissions.length ? (
                      d.permissions.map((p) => (
                        <Badge key={p} tone={p === 'write' ? 'amber' : 'outline'} className="text-[11px]">
                          {PERMISSION_LABEL[p] ?? p}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('Default permissions')}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {!c.managed && !foreign && <ConnectionSharingSection connection={c} className="lg:col-span-2" />}

        {!foreign && c.type === 'mcp_server' && (SCOPE_CHOICE_ADAPTERS as readonly unknown[]).includes(c.config.adapter) && (
          <ConnectionScopeSection connection={c} workspaceId={workspaceId} className="lg:col-span-2" />
        )}

        {c.type === 'mcp_server' && <McpToolsSection connectionId={c.id} connectionFailing={c.status === 'failing'} className="lg:col-span-2" />}

        <Section icon={<Settings2 />} title={t('Configuration')} className="lg:col-span-2">
          {isEmptyObject(c.config) ? <p className="text-sm text-muted-foreground">{t('No configuration.')}</p> : <ConfigView type={c.type} config={c.config} />}
        </Section>
      </div>

      <TechDetails className="mt-6">
        <JsonBlock value={c} />
      </TechDetails>
    </Page>
  );
}
